import type {
  IPlayjectorService,
  InstanceCapabilities,
} from './interfaces/IPlayjectorService';

/**
 * InstanceCapabilitiesProvider (android-multi-instance-host Phase 2).
 *
 * Owns the host's view of Playjector's reported multi-instance capabilities
 * (`provisionedInstanceCount` / `maxGameInstances`): queries
 * `PlayjectorService.getInstanceCapabilities()` at startup and on every
 * Playjector (re)connect, and caches the last good reply.
 *
 * `capabilities()` returns `undefined` while no query has ever succeeded —
 * "unknown" — and consumers (the effective-max clamp, the v2 machine report)
 * treat unknown as a no-op rather than a zero ceiling. A failed query keeps
 * the last good value and logs once per refresh cycle, so an old Playjector
 * build without the capabilities schema degrades to "unknown" with a single
 * warning per reconnect instead of log spam.
 */

type CapabilitiesListener = (capabilities: InstanceCapabilities) => void;

export class InstanceCapabilitiesProvider {
  private lastGood: InstanceCapabilities | undefined;
  private failureLoggedThisCycle = false;
  private started = false;
  private readonly listeners = new Set<CapabilitiesListener>();

  constructor(
    private readonly playjectorService: Pick<IPlayjectorService, 'getInstanceCapabilities'>,
  ) {}

  /** Last good capabilities reply, or `undefined` while unknown. */
  public capabilities(): InstanceCapabilities | undefined {
    return this.lastGood;
  }

  /**
   * Run the initial query and re-query on every Playjector (re)connect. The
   * reconnect trigger is host-electron's `host/notifyPlayjectorInitialized`
   * IPC message — the same signal api/messaging/handlers/ipc.ts uses to
   * re-prime stream/camera state on every (re)initialization. Idempotent.
   */
  public start(): void {
    if (this.started) return;
    this.started = true;
    if (typeof window !== 'undefined') {
      window.playcast?.ipc?.tap('mainMessage', (message) => {
        // Same guard as the PlayjectorService tap: a malformed envelope
        // (null / missing header) is logged and dropped, never dereferenced.
        if (!message?.header) {
          console.warn('[InstanceCapabilities] Ignoring malformed mainMessage envelope (missing header)');
          return;
        }
        if (
          message.header.target === 'host'
          && message.header.action === 'notifyPlayjectorInitialized'
          && !message.header.isReply
        ) {
          this.refresh();
        }
      });
    }
    this.refresh();
  }

  /**
   * Re-query capabilities. A successful reply replaces the cache and notifies
   * listeners; a failure keeps the last good value and logs once per cycle.
   */
  public refresh(): void {
    this.failureLoggedThisCycle = false;
    void this.playjectorService.getInstanceCapabilities().then(
      (capabilities) => {
        this.lastGood = capabilities;
        for (const listener of this.listeners) {
          try {
            listener(capabilities);
          } catch (error) {
            console.error('[InstanceCapabilities] listener error:', error);
          }
        }
      },
      (error) => {
        if (this.failureLoggedThisCycle) return;
        this.failureLoggedThisCycle = true;
        console.warn(
          '[InstanceCapabilities] capabilities query failed — keeping last good value:',
          error,
        );
      },
    );
  }

  /**
   * Subscribe to successful capability refreshes (e.g. to flush the v2 host
   * state report so the machine section picks up fresh values). Returns an
   * unsubscribe function.
   */
  public addListener(listener: CapabilitiesListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}
